import { QuotaType } from '@bpinternal/const'
import {
  Badge,
  Button,
  Separator,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@bpinternal/ui-kit-next'
import { ArrowRight, Minus, Pencil, Plus } from 'lucide-react'
import { FC } from 'react'
import { AutoRechargeStatus, ProductIcon } from '~/features/billing/components'
import { cn, formatters } from '~/utils'
import { AddonDetails, AddonQuotaType, AutoRechargeSettings } from '../types/index'
import { useAutoRechargeDialog } from '../hooks/useAutoRechargeDialog'

type AddonsProps = {
  addons: AddonDetails[]
  addonDeltas: Record<AddonQuotaType, number>
  updateAddonDelta: (addonQuotaName: AddonQuotaType, delta: number) => void
  workspaceId: string
  billingReadonly: boolean
}

export const AddonItems = ({ addons, addonDeltas, updateAddonDelta, workspaceId, billingReadonly }: AddonsProps) => {
  return (
    <div>
      {/* Mobile View */}
      <div className="flex flex-col gap-4 md:hidden">
        {addons.map((addon) => (
          <AddonCards
            key={addon.productId}
            addon={addon}
            addonDeltas={addonDeltas}
            onUpdate={updateAddonDelta}
            workspaceId={workspaceId}
            billingReadonly={billingReadonly}
          />
        ))}
      </div>

      {/* Desktop View */}
      <div className="hidden md:block">
        <Table>
          <TableHeader className="[&_tr]:border-b-0">
            <TableRow className="hover:bg-transparent">
              <TableHead>Feature</TableHead>
              <TableHead className="text-center">Auto Recharge</TableHead>
              <TableHead className="text-center">Quantity</TableHead>
              <TableHead className="text-center">Subtotal</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {addons.map((addon) => (
              <AddonTableRow
                key={addon.productId}
                addon={addon}
                addonDeltas={addonDeltas}
                onUpdate={updateAddonDelta}
                workspaceId={workspaceId}
                billingReadonly={billingReadonly}
              />
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

const AddonCards: FC<{
  addon: AddonDetails
  addonDeltas: Record<AddonQuotaType, number>
  onUpdate: (addonQuotaName: AddonQuotaType, delta: number) => void
  workspaceId: string
  billingReadonly: boolean
}> = ({ addon, addonDeltas, onUpdate, workspaceId, billingReadonly }) => {
  const handleAutoRechargeClick = useAutoRechargeDialog(addon.productId, workspaceId)

  return (
    <div className="flex flex-col gap-4 rounded-2xl bg-neutral-50 p-4">
      <div className="flex justify-between">
        <div className="flex items-center gap-3">
          <ProductIcon
            productQuotaName={addon.quotaName as QuotaType}
            className="size-8 flex-shrink-0 rounded-lg bg-blue-400 p-1.5 text-white"
          />
          <div>
            <span className="text-sm">{addon.displayName}</span>
            <p className="text-xs text-muted-foreground">{addon.description}</p>
          </div>
        </div>
        <div className="font-semibold text-muted-foreground">
          {formatters.$centdollars(
            addon.priceCents * (addon.nextPaymentQuantity + addonDeltas[addon.quotaName] || 0),
            0
          )}
        </div>
      </div>
      <div className="flex items-center justify-between">
        <div className="text-xs text-muted-foreground">Quantity</div>
        <AddonQuantityControls addon={addon} onUpdate={onUpdate} addonDeltas={addonDeltas} />
      </div>
      {addon.autoRecharge.supported && (
        <>
          <Separator />
          <AddonAutoRecharge
            autoRechargeSettings={addon.autoRecharge.settings}
            onClick={handleAutoRechargeClick}
            disabled={billingReadonly}
          />
        </>
      )}
    </div>
  )
}

const AddonTableRow: FC<{
  addon: AddonDetails
  addonDeltas: Record<AddonQuotaType, number>
  onUpdate: (addonQuotaName: AddonQuotaType, delta: number) => void
  workspaceId: string
  billingReadonly: boolean
}> = ({ addon, addonDeltas, onUpdate, workspaceId, billingReadonly }) => {
  const subtotal = (addon.nextPaymentQuantity + addonDeltas[addon.quotaName]) * addon.priceCents
  const handleAutoRechargeClick = useAutoRechargeDialog(addon.productId, workspaceId)

  return (
    <TableRow className="border-b-0 hover:bg-transparent">
      <TableCell>
        <div className="flex items-start gap-3">
          <ProductIcon
            className="size-9 flex-shrink-0 rounded-lg bg-blue-400 p-1.5 text-white"
            productQuotaName={addon.quotaName as QuotaType}
          />
          <div>
            <p className="font-medium">{addon.displayName}</p>
            <p className="text-sm text-muted-foreground">{addon.description}</p>
          </div>
        </div>
      </TableCell>
      <TableCell className="w-[168px] min-w-36 pl-5">
        {addon.autoRecharge.supported ? (
          <AddonAutoRecharge
            autoRechargeSettings={addon.autoRecharge.settings}
            onClick={handleAutoRechargeClick}
            disabled={billingReadonly}
          />
        ) : (
          <div className="-ml-4 flex items-center justify-center gap-1">-</div>
        )}
      </TableCell>
      <TableCell className="w-40">
        <AddonQuantityControls addon={addon} addonDeltas={addonDeltas} onUpdate={onUpdate} />
      </TableCell>
      <TableCell className="w-24">
        <div className="flex flex-col gap-1">
          <span className={cn('text-center text-sm font-normal', subtotal === 0 && 'text-muted-foreground')}>
            {formatters.$centdollars(subtotal, 0)}
          </span>
        </div>
      </TableCell>
    </TableRow>
  )
}

const AddonAutoRecharge: FC<{
  autoRechargeSettings: AutoRechargeSettings[keyof AutoRechargeSettings]
  onClick?: () => void
  disabled?: boolean
}> = ({ autoRechargeSettings, onClick, disabled }) => {
  return (
    <>
      {/* Desktop View */}
      <Button
        variant="ghost"
        className="group hidden w-full flex-1 items-center justify-center gap-1 rounded-lg p-4 transition-colors hover:bg-muted/50 md:flex"
        onClick={onClick}
        disabled={disabled}
      >
        <AutoRechargeStatus settings={autoRechargeSettings} textClassName="text-sm font-normal" />
        {autoRechargeSettings.enabled && (
          <Badge variant="secondary" className="px-1">
            Limit {autoRechargeSettings.limit}
          </Badge>
        )}
        <div className="ml-1 rounded-lg opacity-0 transition-opacity group-hover:opacity-100">
          <Pencil className="!h-4 !w-4" />
        </div>
      </Button>

      {/* Mobile View */}
      <div className="group flex items-center justify-between gap-1 rounded-lg border-2 border-transparent md:hidden">
        <div className="flex items-center gap-1">
          <AutoRechargeStatus
            settings={autoRechargeSettings}
            textClassName="text-xs"
            prefix={<span>Auto Recharge </span>}
          />
          {autoRechargeSettings.enabled && (
            <Badge variant="secondary" className="px-1">
              Limit {autoRechargeSettings.limit}
            </Badge>
          )}
        </div>
        <Button variant="secondary" size="icon" className="ml-1 h-auto p-2" onClick={onClick} disabled={disabled}>
          <Pencil className="!h-4 !w-4" />
        </Button>
      </div>
    </>
  )
}

const AddonQuantityControls: FC<{
  addon: AddonDetails
  addonDeltas: Record<AddonQuotaType, number>
  onUpdate: (addonQuotaName: AddonQuotaType, delta: number) => void
}> = ({ addon, addonDeltas, onUpdate }) => {
  const hasChanges = addon.nextPaymentQuantity + addonDeltas[addon.quotaName] !== addon.currentQuantity
  const isImmediate = addon.nextPaymentQuantity + addonDeltas[addon.quotaName] > addon.currentQuantity

  return (
    <div className="flex w-36 items-center justify-between">
      <Button
        variant="secondary"
        className="my-0.5"
        size="icon"
        onClick={() => onUpdate(addon.quotaName, -1)}
        disabled={addon.nextPaymentQuantity + addonDeltas[addon.quotaName] === 0}
        aria-label={`Decrease ${addon.name} quantity`}
      >
        <Minus className="h-5 w-5" />
      </Button>
      <div className="text-center">
        {hasChanges ? (
          <div className="flex items-center justify-center gap-1">
            <span className="text-muted-foreground">{addon.currentQuantity}</span>
            <ArrowRight className="h-3 w-3 stroke-neutral-400 !stroke-[2.5px]" />
            <span>{addon.nextPaymentQuantity + addonDeltas[addon.quotaName]}</span>
          </div>
        ) : (
          <span>{addon.nextPaymentQuantity + addonDeltas[addon.quotaName]}</span>
        )}
        {hasChanges && (
          <div className="text-xs text-muted-foreground">
            {isImmediate ? (
              'Immediate'
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="cursor-help">Next Month</span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>The current limit will remain active until the next billing cycle.</p>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        )}
      </div>
      <Button
        variant="secondary"
        size="icon"
        className="my-0.5"
        onClick={() => onUpdate(addon.quotaName, 1)}
        aria-label={`Increase ${addon.name} quantity`}
      >
        <Plus className="h-5 w-5" />
      </Button>
    </div>
  )
}
